import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { useCRM } from '../../context/CRMContext';
import { defaultKnowledgeArticles } from '../../data/knowledgeArticles';

const HelpDrawer = () => {
  const { isHelpOpen, toggleHelp, knowledgeArticles } = useCRM();
  const location = useLocation();
  const navigate = useNavigate();
  const articles = knowledgeArticles?.length ? knowledgeArticles : defaultKnowledgeArticles;

  // Smart kontekst-matching: Eksakt match prioriteres over delvis match
  const currentContextArticles = useMemo(() => {
    // 1. Prøv å finne eksakt URL match (f.eks /dashboard/compliance)
    const exactMatches = articles.filter(article => 
      article.context?.some(path => location.pathname === path)
    );
    
    if (exactMatches.length > 0) return exactMatches;

    // 2. Fallback til startsWith for under-sider (f.eks /companies/123 -> matches /companies)
    return articles.filter(article => 
      article.context?.some(path => path !== '/' && location.pathname.startsWith(path))
    );
  }, [articles, location.pathname]);

  const otherArticles = useMemo(() => {
    const currentIds = currentContextArticles.map(a => a.id);
    return articles.filter(article => !currentIds.includes(article.id));
  }, [articles, currentContextArticles]);

  const handleArticleClick = (id) => {
    toggleHelp();
    navigate(`/knowledge-base?article=${id}`);
  };

  return (
    <AnimatePresence>
      {isHelpOpen && (
        <>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={toggleHelp}
            className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-[90]"
          />
          <motion.div 
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed inset-y-0 right-0 w-full md:w-96 bg-white shadow-2xl z-[100] border-l border-slate-200 flex flex-col"
          >
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600">
                  <SafeIcon icon="💡" className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="font-black text-slate-900 text-lg">Hjelpesenter</h2>
                  <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">Synkronisert veiledning</p>
                </div>
              </div>
              <button onClick={toggleHelp} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-400">
                <SafeIcon icon="✕" className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-8">
              
              <div>
                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <SafeIcon icon="🎯" className="w-3 h-3" /> Relatert til denne siden
                </h3>
                
                {currentContextArticles.length > 0 ? (
                  <div className="space-y-3">
                    {currentContextArticles.map(article => (
                      <div 
                        key={article.id}
                        onClick={() => handleArticleClick(article.id)}
                        className="p-5 bg-indigo-50 border-2 border-indigo-100 rounded-xl cursor-pointer hover:bg-indigo-100 hover:border-indigo-300 transition-all group shadow-sm"
                      >
                        <h4 className="font-black text-indigo-900 text-base mb-2 group-hover:text-indigo-700">{article.title}</h4>
                        <p className="text-xs text-indigo-800/80 line-clamp-3 leading-relaxed mb-4">{article.description}</p>
                        <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-indigo-600 bg-white p-2 rounded-lg justify-center shadow-sm group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                          Les full veileder <SafeIcon icon="➡️" className="w-3 h-3" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 text-center bg-slate-50 rounded-2xl border border-slate-100 border-dashed">
                    <p className="text-sm text-slate-500 font-medium">Ingen spesifikke guider funnet for denne URL-en.</p>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <SafeIcon icon="📚" className="w-3 h-3" /> Andre emner i Kunnskapsbasen
                </h3>
                <div className="space-y-2">
                  {otherArticles.slice(0, 5).map(article => (
                    <button 
                      key={article.id}
                      onClick={() => handleArticleClick(article.id)}
                      className="w-full text-left p-3 hover:bg-slate-50 rounded-lg transition-colors flex items-center justify-between group border border-transparent hover:border-slate-100"
                    >
                      <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">{article.title}</span>
                      <SafeIcon icon="📄" className="w-4 h-4 text-slate-300 group-hover:text-primary-500" />
                    </button>
                  ))}
                </div>
              </div>

            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50">
              <button 
                onClick={() => { toggleHelp(); navigate('/knowledge-base'); }}
                className="w-full py-3 bg-white border border-slate-200 text-slate-700 font-black text-xs uppercase tracking-widest rounded-xl hover:bg-slate-100 hover:border-slate-300 transition-all shadow-sm"
              >
                Gå til full Kunnskapsbase
              </button>
            </div>
            
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default HelpDrawer;